import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth, type SignupRole } from '@/contexts/AuthContext';
import { AuthLoading } from '@/components/auth/AuthGuard';
import AuthShell from '@/components/auth/AuthShell';

const ROLE_OPTIONS: { key: SignupRole; label: string; desc: string; icon: string }[] = [
  { key: 'teacher', label: '선생님', desc: '여러 학생의 오답노트를 학생별로 관리', icon: 'ri-user-star-line' },
  { key: 'student', label: '학생', desc: '내 오답노트만 시험별로 관리', icon: 'ri-graduation-cap-line' },
];

export default function SignupPage() {
  const { session, profile, loading, profileLoading, signUp } = useAuth();
  const [role, setRole] = useState<SignupRole>('teacher');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading || (session && profileLoading)) return <AuthLoading />;
  if (session) return <Navigate to={profile?.status === 'approved' ? '/' : '/pending'} replace />;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed || !password) {
      setError('이메일과 비밀번호를 모두 입력해 주세요.');
      return;
    }
    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 해요.');
      return;
    }
    if (password !== confirm) {
      setError('비밀번호가 서로 일치하지 않아요.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await signUp(trimmed, password, role);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    // 가입 즉시 로그인되어 승인 대기 화면으로 이동한다.
  };

  return (
    <AuthShell
      title="회원가입 신청"
      subtitle="가입을 신청하면 운영자가 확인 후 승인해요. 승인되면 바로 오답노트를 사용할 수 있어요."
      footer={
        <>
          이미 계정이 있나요?{' '}
          <Link to="/login" className="font-bold text-primary-700 hover:underline">
            로그인
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <span className="mb-1.5 block text-xs font-semibold text-foreground-600">가입 유형</span>
          <div className="grid grid-cols-2 gap-2.5">
            {ROLE_OPTIONS.map((opt) => {
              const active = role === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setRole(opt.key)}
                  className={`flex cursor-pointer flex-col items-start gap-1.5 rounded-md border px-3 py-3 text-left transition ${
                    active
                      ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-100'
                      : 'border-background-300 bg-background-50 hover:border-primary-300 hover:bg-primary-50/40'
                  }`}
                >
                  <span className={`flex h-8 w-8 items-center justify-center rounded-md ${active ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'}`}>
                    <i className={`${opt.icon} text-base`}></i>
                  </span>
                  <span className="text-sm font-bold text-foreground-900">{opt.label}</span>
                  <span className="text-[11px] leading-snug text-foreground-500">{opt.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="signup-email" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            이메일
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-mail-line text-base text-foreground-400"></i>
            <input
              id="signup-email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
        </div>

        <div>
          <label htmlFor="signup-password" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            비밀번호 (6자 이상)
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-line text-base text-foreground-400"></i>
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 transition hover:bg-background-200 hover:text-foreground-700"
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
            >
              <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="signup-confirm" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            비밀번호 확인
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-2-line text-base text-foreground-400"></i>
            <input
              id="signup-confirm"
              type={showPassword ? 'text' : 'password'}
              name="confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="비밀번호 다시 입력"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-800">
            <i className="ri-error-warning-line mt-0.5 text-sm"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-xs leading-relaxed text-foreground-600">
          <i className="ri-information-line mt-0.5 text-sm text-secondary-700"></i>
          <span>이메일 인증 없이 바로 가입되고, 운영자 승인 전까지는 오답노트를 사용할 수 없어요.</span>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              신청 중...
            </>
          ) : (
            <>
              <i className="ri-user-add-line text-base"></i>
              가입 신청하기
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}